import { Component, OnInit, OnDestroy } from '@angular/core';
import { TruckOwnerService } from '../truck-owner.service';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { QuoteBidComponent } from '../quote-bid/quote-bid.component';

export interface Notification {
  type: 'DRIVER_APPROVAL' | 'ACTIVE_QUOTE' | 'ACTIVE_WO' | 'WO_ADD_TRUCK' | 'WORK_ORDER_CREATED' | 'QUOTE_CREATED' | 'RANKING_UPDATED';
  title: string;
  message: string;
  timestamp: Date;
  data?: any;
}

@Component({
  selector: 'app-notifications',
  templateUrl: './notifications.component.html',
  styleUrls: ['./notifications.component.css']
})
export class NotificationsComponent implements OnInit, OnDestroy {
  token: any;
  notifications: Notification[] = [];
  isLoading = false;
  timerInterval: any;

  constructor(private service: TruckOwnerService, private router: Router, private dialog: MatDialog) { }

  ngOnInit(): void {
    this.token = sessionStorage.getItem("to_token");
    this.getNotifications();
    sessionStorage.setItem("tripType", "notifications");

    this.timerInterval = setInterval(() => {
      this.updateCountdowns();
    }, 1000);
  }

  ngOnDestroy(): void {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
    }
  }

  updateCountdowns() {
    const now = new Date().getTime();
    this.notifications.forEach(note => {
      if (note.data.expiresAt) {
        const expiresAt = new Date(note.data.expiresAt).getTime();
        const diff = expiresAt - now;
        if (diff > 0) {
          const hours = Math.floor(diff / (1000 * 60 * 60));
          const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
          const seconds = Math.floor((diff % (1000 * 60)) / 1000);

          let countdownStr = '';
          if (hours > 0) countdownStr += `${hours}h `;
          if (minutes > 0 || hours > 0) countdownStr += `${minutes}m `;
          countdownStr += `${seconds}s`;

          note.data.countdown = countdownStr.trim();
        } else {
          // add expired in string 
          note.data.countdown = ' ';
        }
      }
    });
  }

  getNotifications() {
    this.isLoading = true;
    this.service.getNotificationList(this.token).subscribe((res: any) => {
      this.isLoading = false;
      if (res.status === 200 && res.body) {
        const apiNotifications = res.body.data || res.body;

        this.notifications = apiNotifications.map((note: any) => {
          let type = note.notificationData?.eventType;
          let data = note.notificationData || {};
          let metadata = data.metadata || {};

          // Mapping
          if (type === 'DRIVER_TRUCK_REQUEST') {
            type = 'DRIVER_APPROVAL';
            data.refId = note.notificationData.sourceId;
            data.name = metadata.driver_name || (data.message?.split(' ')[1] === 'undefined' ? 'Unknown Driver' : data.message?.split(' ')[1]);
            data.driver_mobile = metadata.driver_mobile;
            data.driver_id = metadata.driver_id;
          } else if (type === 'WORK_ORDER_CREATED' || type === 'ACTIVE_WO' || type === 'WO_ADD_TRUCK' || type === 'RANKING_UPDATED') {
            data.woId = note.notificationData.sourceId;
          } else if (type === 'QUOTE_CREATED' || type === 'ACTIVE_QUOTE') {
            data.quoteId = note.notificationData.sourceId;
          }

          // Ensure route and dates are easily accessible
          const from = metadata.from_location || data.origin || metadata.origin || '';
          const to = metadata.to_location || data.destination || metadata.destination || '';
          const pickupDate = metadata.pickup_date || data.pickup_date || metadata.pickupDate || data.pickupDate;

          return {
            type: type,
            title: note.notificationData?.title || note.title,
            message: note.notificationData?.message || note.message,
            timestamp: new Date(note.createdAt || new Date()),
            data: {
              ...data,
              from: from,
              to: to,
              pickupDate: pickupDate ? new Date(pickupDate) : null,
              expiresAt: metadata.expiresAt || data.expiresAt || metadata.pickup_date || data.pickup_date || null,
              countdown: ''
            }
          };
        });

        // Initial setup for countdowns
        this.updateCountdowns();
      }
    }, (error) => {
      this.isLoading = false;
      console.error('Error fetching notifications:', error);
    });
  }

  onAcceptDriver(notification: Notification) {
    if (!notification.data.refId) {
      console.error('No Ref ID for approval');
      return;
    }
    this.service.driverApproval(this.token, 'accepted', notification.data.refId).subscribe((res: any) => {
      if (res.status === 200 || res.status === 201) {
        this.getNotifications(); // Refresh list
      }
    });
  }

  onRejectDriver(notification: Notification) {
    if (!notification.data.refId) {
      console.error('No Ref ID for rejection');
      return;
    }
    this.service.driverApproval(this.token, 'rejected', notification.data.refId).subscribe((res: any) => {
      if (res.status === 200 || res.status === 201) {
        this.getNotifications(); // Refresh list
      }
    });
  }

  onViewQuote(notification: Notification) {
    console.log('Viewing quote:', notification.data.quoteId);
  }

  onViewWorkOrder(notification: Notification) {
    const woId = notification.data.woId || notification.data.metadata?.workOrderNo || notification.data.metadata?.workorderId;
    this.router.navigate(['transporter/pre-orders'], { queryParams: { workOrderId: woId } });
  }

  onAddTruck(notification: Notification) {
    const woId = notification.data.woId || notification.data.metadata?.workOrderNo || notification.data.metadata?.workorderId;
    this.router.navigate(['transporter/pre-orders'], { queryParams: { workOrderId: woId } });
  }

  onBid(notification: Notification) {
    if (notification.type === 'QUOTE_CREATED' || notification.type === 'ACTIVE_QUOTE') {
      const dialogRef = this.dialog.open(QuoteBidComponent, {
        // height: "90vh",
        width: "40vw",
        autoFocus: false,
        data: notification.data
      });

      dialogRef.afterClosed().subscribe(result => {
        if (result === 'success') {
          this.getNotifications();
        }
      });
    } else {
      // Work Order bidding navigates to pre-orders
      const woId = notification.data.woId || notification.data.metadata?.workOrderNo || notification.data.metadata?.workorderId;
      this.router.navigate(['transporter/pre-orders'], { queryParams: { workOrderId: woId } });
    }
  }

}
